import { isWorkspaceAdmin, type WorkspaceMembership } from '../api/types';
import { isFirmWorkspace, type WorkspaceKind } from '../workspace/useWorkspaceKind';
import type { AdvisorForFirm } from './types';

/**
 * Is the caller an ADMIN of the firm workspace they are standing in, AND is that the same
 * firm the advisor reads resolve to? Originally (steward-app#1361) the one predicate behind
 * both the "Your firm" page's route gate and its nav item.
 *
 * ⚠️ **As of steward-app#1742, `RequireFirmAdmin` and `Layout.tsx`'s nav item call
 * {@link isFirmAdminWorkspace} instead, not this function** — that predicate is wide enough
 * to admit the loading/retry/mismatch states those two now render for a lookup that has not
 * settled, or has settled on a different firm than this workspace's own. This function is
 * still what `RequireFirmAdmin` uses INTERNALLY to decide whether the settled answer matches
 * this workspace closely enough to render the real page rather than one of those states — see
 * that file for the current call graph.
 *
 * ⚠️ **UX gating, not authorization.** Every read and write the page makes is decided by
 * appcore's authorizer: `/members` and `/invites` need `mem:manage` in the workspace named by
 * `X-Workspace-Id`; the firm-rename write (`FirmDetailsSection`, steward-app#1730) is gated by
 * its own capability probe instead (`YourFirm.tsx`'s `FIRM_NAME_CAPABILITIES` /
 * `FIRM_NAME_CAPABILITY_QUERY`, `mem:manage`+`ws:manage` in the firm workspace — corrected
 * steward-app#1733 second fix round, delta review, item 5: an earlier version of this line
 * named a `FIRM_NAME_PROBE` that is not an exported constant, only a test-file literal in
 * `YourFirm.test.tsx`). A caller who fails this predicate and types the URL anyway gets an
 * explanation instead of the page; a caller who forges their way past it in devtools gets
 * 403s. Nothing here is a reason any of that data is safe.
 *
 * ⚠️ **This page no longer reads `/advisor/pending` or writes the activate route at all**
 * (steward-app#1733 fix round; an earlier version of this paragraph still named both). The
 * firm's own pending queue and its Accept action moved to the Clients tab's `WaitingList`,
 * gated by `canAcceptQueue` there, not by `isFirmAdminHere` — a firm admin and a firm member
 * who merely holds both `steward-firm-staff` and `steward-firm-authority` see the same rows on
 * that separate page, neither of which needs to pass THIS predicate first.
 *
 * Three conjuncts, each closing a different wrong page:
 *
 *   - **an active membership is known.** `null` while `/me` resolves; the gate renders nothing
 *     in that window rather than a refusal it would take back a moment later (the same rule
 *     `RequireAdmin` follows).
 *   - **`isWorkspaceAdmin(roleIds)`**, the same test `RequireAdmin` and `useIsAdmin` use. A
 *     firm's staff and advisors do not manage its people.
 *   - **`advisorFor.firmWorkspaceId === activeWorkspaceId`, and NOT `useWorkspaceKind() ===
 *     'firm'`.** This is the one that differs from `Settings.tsx`'s `inFirmWorkspace`, and on
 *     purpose. The page mixes two scoping models: People and Add a person are scoped by
 *     `X-Workspace-Id` (the ACTIVE workspace), while Firm details (`FirmDetailsSection`) is
 *     scoped by the firm the façade resolves from the caller's memberships
 *     (`advisorFirmContext`, narrowed by `?firmId=`). Someone at two firms, standing in firm
 *     A's workspace with firm B chosen in this tab, would see A's people above B's firm-name
 *     heading. Requiring the resolved firm's own workspace to BE the active one makes both
 *     halves name the same firm. A pack-only "this is a firm workspace" answer cannot, which
 *     is why the #700 fallback half of `inFirmWorkspace` is the half kept here and the pack
 *     half is not.
 *
 * Not keyed on `via.firmId` or any other membership provenance (steward-app#1343 correction 2):
 * it reads only the caller's own role ids and a server-resolved firm.
 */
export function isFirmAdminHere(
  activeMembership: Pick<WorkspaceMembership, 'roleIds'> | null | undefined,
  activeWorkspaceId: string | null | undefined,
  advisorFor: Pick<AdvisorForFirm, 'firmWorkspaceId'> | null | undefined,
): boolean {
  if (!activeMembership || !activeWorkspaceId || !advisorFor) return false;
  if (!isWorkspaceAdmin(activeMembership.roleIds ?? [])) return false;
  return advisorFor.firmWorkspaceId === activeWorkspaceId;
}

/**
 * Is this workspace one a firm admin might have something to reach on the Firm page —
 * steward-app#1742. Wider than {@link isFirmAdminHere} on purpose: this admits the
 * loading / retry / "choose the right firm" states `RequireFirmAdmin` renders while
 * `GET /advisor/firms` is still resolving, has failed, or has resolved to a firm OTHER than
 * the one this workspace is (an admin staff at two firms, standing in firm A with firm B's
 * lookup answer cached) — none of which `isFirmAdminHere` admits, because it additionally
 * requires the lookup to have SETTLED and matched this exact workspace.
 *
 * Before #1742, `Layout.tsx`'s nav item and `RequireFirmAdmin`'s route gate both called
 * `isFirmAdminHere` alone, which is the bug this predicate exists to close: a firm admin
 * whose lookup had not yet resolved, had failed, or was still answering for a firm they were
 * previously acting as, saw no "Firm" link and a route that refused them outright — with
 * Settings > Members ALSO hidden for them once #1742's `isKnownFamilyWorkspace`
 * (`workspace/useWorkspaceKind.ts`) took over that gate, because that predicate correctly
 * hides Members for any KNOWN firm workspace without waiting on this lookup at all. Without
 * this wider predicate, that admin would have had no surface anywhere to reach the four
 * firm-capability checkboxes.
 *
 * Three conjuncts:
 *   - **`isWorkspaceAdmin(roleIds)`** — a firm's staff and advisors do not manage its people,
 *     same as `isFirmAdminHere`.
 *   - **the degraded-packs exception** (security review, steward-app#1742, 2026-09-23):
 *     `packsResolved === false` with the firm lookup ALSO unsettled (`firmLookupUnsettled`,
 *     pass `isLoading || isError` from `useAdvisorFirm()`) admits the caller outright, ahead of
 *     `isFirmWorkspace` below, rather than asking that stand-in to answer with nothing to go on.
 *     Without this, that quadrant fell through to `isFirmWorkspace`'s stand-in arm, which needs
 *     `advisorFor` truthy to answer `true` and reads `false` while it is still `null` mid-flight
 *     — so an admin whose packs read degraded on the very request that would have told
 *     `RequireFirmAdmin` whether they run a firm got its hard "this page is for your firm's
 *     admins" refusal instead of the loading state every OTHER unsettled-lookup case gets. This
 *     is the mirror image of `isKnownFamilyWorkspace`'s own early return for the identical
 *     quadrant: that predicate answers `false` (hidden) so Members does not show for someone who
 *     may turn out to run a firm; this one answers `true` (reachable) so the Firm page is there
 *     to land on once `RequireFirmAdmin`'s OWN `isLoading` check (below the call into this
 *     function) decides what to render meanwhile. Not answering the underlying "is this a firm
 *     workspace" question at all — genuinely unknowable here — only "is there something worth
 *     waiting on before refusing".
 *   - **`isFirmWorkspace(...)`** — a fact about the WORKSPACE (the pack, or, during a
 *     resolving membership read with a SETTLED lookup, the #700 id-comparison stand-in), for
 *     every quadrant the exception above does not intercept first.
 *
 * ⚠️ **UX gating only**, exactly as `isFirmAdminHere`'s own header says. Reaching the page is
 * necessary, not sufficient, for seeing or changing anything on it — `RequireFirmAdmin` still
 * decides, once mounted, whether to show the real page, a chooser, or a retry/mismatch state,
 * and appcore authorizes every read and write regardless.
 */
export function isFirmAdminWorkspace(
  activeMembership: Pick<WorkspaceMembership, 'roleIds' | 'packsResolved'> | null | undefined,
  workspaceKind: WorkspaceKind,
  activeWorkspaceId: string | null | undefined,
  advisorFor: Pick<AdvisorForFirm, 'firmWorkspaceId'> | null | undefined,
  firmLookupUnsettled: boolean,
): boolean {
  if (!activeMembership) return false;
  if (!isWorkspaceAdmin(activeMembership.roleIds ?? [])) return false;
  if (activeMembership.packsResolved === false && firmLookupUnsettled) return true;
  return isFirmWorkspace(workspaceKind, activeMembership, activeWorkspaceId, advisorFor);
}
